import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Receipt, ExternalLink, AlertCircle, CheckCircle2, Clock, Layers, Users, Activity, CreditCard, ArrowRightCircle, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { findConsultingSubcategory } from '../../../../shared/consultingServices';
import useAuthStore from '../../context/authStore';
import { invoicesAPI, organizationsAPI } from '../../services/api';

const getServiceEntitlements = (...sources) => {
  for (const source of sources) {
    if (source?.serviceEntitlements) return source.serviceEntitlements;
    if (source?.organization?.serviceEntitlements) return source.organization.serviceEntitlements;
    if (source?.billingOverview?.serviceEntitlements) return source.billingOverview.serviceEntitlements;
  }
  return {};
};

const hasCommercialEntitlements = (entitlements = {}) => {
  const consulting = entitlements?.consulting || {};
  const staffing = entitlements?.staffing || {};
  const lms = entitlements?.lms || {};

  return Boolean(
    consulting?.enabled ||
    staffing?.enabled ||
    lms?.enabled ||
    lms?.subscription ||
    (Array.isArray(consulting?.categories) && consulting.categories.length > 0) ||
    (Array.isArray(consulting?.effectiveServices) && consulting.effectiveServices.length > 0) ||
    Number(lms?.usage?.activeCourseAccessCount || 0) > 0 ||
    Number(lms?.usage?.seatsLimit || 0) > 0,
  );
};

const formatScopeLabel = (scopeType = 'facility_scoped') => (
  scopeType === 'org_wide' ? 'Org-wide' : 'Facility-scoped'
);

const formatDeliveryLabel = (deliveryMode = 'stratel_hosted') => (
  deliveryMode === 'external_delivery' ? 'External delivery' : 'Stratel hosted'
);

const formatDistributionLabel = (distributionMode = 'assignment_only') => (
  String(distributionMode || 'assignment_only').replace(/_/g, ' ')
);

const formatCoverageText = (coverage = {}) => {
  if (!coverage) return '-';
  const scopeLabel = formatScopeLabel(coverage.scopeType);
  const totalFacilityCount = Number(coverage.totalFacilityCount || 0);
  const coveredFacilityCount = Number(
    coverage.coveredFacilityCount || coverage.includedFacilityCount || 0,
  );
  if (coverage.scopeType === 'org_wide') {
    return totalFacilityCount > 0
      ? `${scopeLabel} · ${totalFacilityCount} of ${totalFacilityCount} facilities covered`
      : `${scopeLabel} · all facilities covered`;
  }
  if (totalFacilityCount > 0) {
    return `${scopeLabel} · ${coveredFacilityCount} of ${totalFacilityCount} facilities covered`;
  }
  return `${scopeLabel} · ${coveredFacilityCount || 1} ${coveredFacilityCount === 1 ? 'facility' : 'facilities'} covered`;
};

const formatFacilityNames = (coverage = {}) => {
  const names = Array.isArray(coverage?.facilityNames)
    ? coverage.facilityNames.filter(Boolean)
    : [];
  return names.length ? names.join(', ') : '';
};

const formatCommercialAppliesTo = (coverage = {}) => {
  const appliesTo = String(coverage?.appliesTo || '').trim();
  const includedFacilityCount = Number(coverage?.includedFacilityCount || 0);
  if (appliesTo === 'all_active_facilities') return 'All active facilities';
  if (appliesTo === 'selected_facilities') {
    return includedFacilityCount > 0
      ? `Selected facilities (${includedFacilityCount})`
      : 'Selected facilities';
  }
  if (appliesTo === 'facility_count_only') {
    return includedFacilityCount > 0
      ? `${includedFacilityCount} facilities in commercial scope`
      : 'Facility count to be finalized';
  }
  return 'Organization-level';
};

const formatSeatPool = (usage = {}) => (
  usage?.seatsLimit ? `${usage.seatsLimit}` : 'Unlimited'
);

export default function InvoicesPage() {
  const { user } = useAuthStore();
  const [paymentView, setPaymentView] = useState('all'); // all, unpaid, overdue, partial, paid
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showAllSubcategories, setShowAllSubcategories] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState('25');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState('25');
  const organizationId = user?.organizationId?._id || user?.organizationId || '';

  const { data, isLoading } = useQuery({
    queryKey: ['invoices'],
    queryFn: () => invoicesAPI.getAll({}),
    select: (res) => res.data,
  });
  const { data: overviewData } = useQuery({
    queryKey: ['billing-overview'],
    queryFn: () => invoicesAPI.getBillingOverview({}),
    select: (res) => res.data,
    retry: false,
  });
  const { data: organizationData } = useQuery({
    queryKey: ['billing-organization', organizationId],
    queryFn: () => organizationsAPI.getById(organizationId),
    select: (res) => res.data?.organization,
    enabled: Boolean(organizationId),
    retry: false,
  });

  const statusColors = {
    draft: 'badge-secondary',
    sent: 'badge-primary',
    viewed: 'badge-primary',
    partial: 'badge-warning',
    paid: 'badge-success',
    overdue: 'badge-danger',
    cancelled: 'badge-secondary',
  };

  const getBillToName = (invoice) => {
    if (invoice?.billingParty?.name) return invoice.billingParty.name;
    if (invoice?.billingParty?.type === 'facility') {
      return invoice?.facilityId?.name || invoice?.clientId?.name || 'N/A';
    }
    return invoice?.clientId?.name || 'N/A';
  };

  const getBillToMeta = (invoice) => {
    return [
      invoice?.billingParty?.contactEmail || invoice?.billingEmail || '',
      invoice?.billingParty?.type === 'facility' && invoice?.facilityId?.name
        ? `Facility: ${invoice.facilityId.name}`
        : '',
    ]
      .filter(Boolean)
      .join(' • ');
  };

  // Payment status categorization
  const unpaidStatuses = ['draft', 'sent', 'viewed'];
  const overdueStatuses = ['overdue'];
  const partialStatuses = ['partial'];
  const paidStatuses = ['paid'];

  const filteredInvoices = useMemo(() => {
    const invoices = data?.invoices || [];
    
    if (paymentView === 'unpaid') {
      return invoices.filter(inv => unpaidStatuses.includes(inv.status));
    } else if (paymentView === 'overdue') {
      return invoices.filter(inv => overdueStatuses.includes(inv.status));
    } else if (paymentView === 'partial') {
      return invoices.filter(inv => partialStatuses.includes(inv.status));
    } else if (paymentView === 'paid') {
      return invoices.filter(inv => paidStatuses.includes(inv.status));
    }
    
    return invoices;
  }, [data?.invoices, paymentView]);

  // Calculate payment status counts
  const paymentCounts = useMemo(() => {
    const invoices = data?.invoices || [];
    return {
      all: invoices.length,
      unpaid: invoices.filter(inv => unpaidStatuses.includes(inv.status)).length,
      overdue: invoices.filter(inv => overdueStatuses.includes(inv.status)).length,
      partial: invoices.filter(inv => partialStatuses.includes(inv.status)).length,
      paid: invoices.filter(inv => paidStatuses.includes(inv.status)).length,
    };
  }, [data?.invoices]);

  useEffect(() => {
    setPage(1);
  }, [paymentView, pageSize]);

  useEffect(() => {
    setHistoryPage(1);
  }, [showPaymentHistory, historyPageSize, overviewData?.paymentHistory?.length]);

  const invoicePagination = useMemo(() => {
    const total = filteredInvoices.length;
    if (pageSize === 'all') {
      return { total, pages: 1, currentPage: 1, items: filteredInvoices };
    }
    const limit = Math.max(1, Number(pageSize) || 25);
    const pages = Math.max(1, Math.ceil(total / limit));
    const currentPage = Math.min(page, pages);
    const start = (currentPage - 1) * limit;
    return {
      total,
      pages,
      currentPage,
      items: filteredInvoices.slice(start, start + limit),
    };
  }, [filteredInvoices, page, pageSize]);

  const historyPagination = useMemo(() => {
    const history = overviewData?.paymentHistory || [];
    const total = history.length;
    if (historyPageSize === 'all') {
      return { total, pages: 1, currentPage: 1, items: history };
    }
    const limit = Math.max(1, Number(historyPageSize) || 25);
    const pages = Math.max(1, Math.ceil(total / limit));
    const currentPage = Math.min(historyPage, pages);
    const start = (currentPage - 1) * limit;
    return {
      total,
      pages,
      currentPage,
      items: history.slice(start, start + limit),
    };
  }, [overviewData?.paymentHistory, historyPage, historyPageSize]);

  // keep billing page aligned with Settings > Service Access: prefer billing overview
  // when available, but fall back to the enriched organization entitlements.
  const organizationEntitlements = getServiceEntitlements(organizationData);
  const hasOverviewEntitlements = hasCommercialEntitlements(overviewData?.serviceEntitlements);
  const hasOrganizationEntitlements = hasCommercialEntitlements(organizationEntitlements);
  const isUsingOrganizationFallback = !hasOverviewEntitlements && hasOrganizationEntitlements;
  const billingAccessRestricted = Boolean(data?.billingAccessRestricted || overviewData?.billingAccessRestricted);
  const facilityEntitlements = isUsingOrganizationFallback
    ? organizationEntitlements
    : (overviewData?.serviceEntitlements || {});
  const hasFacilityScopedBilling = Array.isArray(overviewData?.billingFacilities) && overviewData.billingFacilities.length > 0;
  const inheritedEntitlements = overviewData?.inheritedServiceEntitlements || (
    !isUsingOrganizationFallback && hasFacilityScopedBilling && hasOrganizationEntitlements
      ? organizationEntitlements
      : null
  );

  const consultingEffectiveServices = useMemo(() => {
    const consulting = facilityEntitlements?.consulting || {};
    const provided = Array.isArray(consulting?.effectiveServices) ? consulting.effectiveServices.filter(Boolean) : [];
    if (provided.length) return provided;
    const specific = Array.isArray(consulting?.specificServices) ? consulting.specificServices.filter(Boolean) : [];
    const enabledSubcatNames = Array.isArray(consulting?.subcategories)
      ? consulting.subcategories.map((s) => (typeof s === 'string' ? s : s?.name)).filter(Boolean)
      : [];
    // dedupe case-insensitively, preserving first casing
    const result = [];
    const seen = new Set();
    [...specific, ...enabledSubcatNames].forEach((entry) => {
      let str = String(entry || '').trim();
      const match = findConsultingSubcategory(str);
      if (match?.subcategoryLabel) str = match.subcategoryLabel;
      const key = str.toLowerCase();
      if (key && !seen.has(key)) {
        seen.add(key);
        result.push(str);
      }
    });
    return result;
  }, [facilityEntitlements?.consulting]);
  const inheritedConsultingEffectiveServices = useMemo(() => {
    const consulting = inheritedEntitlements?.consulting || {};
    const provided = Array.isArray(consulting?.effectiveServices) ? consulting.effectiveServices.filter(Boolean) : [];
    if (provided.length) return provided;
    const specific = Array.isArray(consulting?.specificServices) ? consulting.specificServices.filter(Boolean) : [];
    const enabledSubcatNames = Array.isArray(consulting?.subcategories)
      ? consulting.subcategories.map((s) => (typeof s === 'string' ? s : s?.name)).filter(Boolean)
      : [];
    const result = [];
    const seen = new Set();
    [...specific, ...enabledSubcatNames].forEach((entry) => {
      let str = String(entry || '').trim();
      const match = findConsultingSubcategory(str);
      if (match?.subcategoryLabel) str = match.subcategoryLabel;
      const key = str.toLowerCase();
      if (key && !seen.has(key)) {
        seen.add(key);
        result.push(str);
      }
    });
    return result;
  }, [inheritedEntitlements?.consulting]);

  const consultingCategories = useMemo(() => {
    const categories = facilityEntitlements?.consulting?.categories;
    if (!Array.isArray(categories)) return [];
    // remove falsy and dedupe case-insensitive
    const out = [];
    const seen = new Set();
    categories.forEach((c) => {
      const str = String(c || '').trim();
      const key = str.toLowerCase();
      if (str && !seen.has(key)) {
        seen.add(key);
        out.push(str);
      }
    });
    return out;
  }, [facilityEntitlements?.consulting?.categories]);
  const inheritedConsultingCategories = useMemo(() => {
    const categories = inheritedEntitlements?.consulting?.categories;
    if (!Array.isArray(categories)) return [];
    const out = [];
    const seen = new Set();
    categories.forEach((c) => {
      const str = String(c || '').trim();
      const key = str.toLowerCase();
      if (str && !seen.has(key)) {
        seen.add(key);
        out.push(str);
      }
    });
    return out;
  }, [inheritedEntitlements?.consulting?.categories]);

  const lmsPlanLabel = facilityEntitlements?.lms?.subscription?.pricingModel || 'No active plan';
  const lmsSeatsUsed = facilityEntitlements?.lms?.usage?.seatsUsed ?? 0;
  const lmsSeatsLimit = facilityEntitlements?.lms?.usage?.seatsLimit ?? null;
  const lmsSeatsRemaining = facilityEntitlements?.lms?.usage?.seatsRemaining ?? 'unlimited';
  const inheritedLmsPlanLabel = inheritedEntitlements?.lms?.subscription?.pricingModel || null;
  const inheritedLmsSeatsUsed = inheritedEntitlements?.lms?.usage?.seatsUsed ?? 0;
  const inheritedLmsSeatsLimit = inheritedEntitlements?.lms?.usage?.seatsLimit ?? null;
  const inheritedLmsSeatsRemaining = inheritedEntitlements?.lms?.usage?.seatsRemaining ?? 'unlimited';
  const hideOrgWideUsage = isUsingOrganizationFallback;
  const lmsCoverage = facilityEntitlements?.lms?.coverage || {};
  const lmsSeatActivity = facilityEntitlements?.lms?.seatActivity || {};
  const inheritedLmsCoverage = inheritedEntitlements?.lms?.coverage || {};
  const inheritedSeatActivity = inheritedEntitlements?.lms?.seatActivity || {};

  const totalOutstanding = data?.invoices?.reduce((sum, inv) => sum + (inv.amountDue || 0), 0) || 0;

  const visibleCategoryChips = showAllCategories ? consultingCategories : consultingCategories.slice(0, 3);
  const visibleSubcategoryChips = showAllSubcategories ? consultingEffectiveServices : consultingEffectiveServices.slice(0, 4);
  const remainingCategoryCount = Math.max(0, consultingCategories.length - visibleCategoryChips.length);
  const remainingSubcategoryCount = Math.max(0, consultingEffectiveServices.length - visibleSubcategoryChips.length);

  // Facility-scoped users should only see Consulting Entitlements if the consulting
  // scope covers their facility (i.e. not strictly org/HQ-only).
  const isFacilityUser = ['facility_admin', 'facility_manager'].includes(user?.role);
  const consultingAppliesTo = facilityEntitlements?.consulting?.coverage?.appliesTo;
  const consultingIsOrgOnly = consultingAppliesTo === 'organization' || (!consultingAppliesTo && !facilityEntitlements?.consulting?.enabled);
  const showConsultingEntitlements = !isFacilityUser || !consultingIsOrgOnly;

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-display font-bold text-secondary-900">Billing & Subscriptions</h1>
          <p className="mt-1 text-secondary-600">Plans, entitlements, payment methods, invoices, and payment history.</p>
          {Array.isArray(overviewData?.billingFacilities) && overviewData.billingFacilities.length > 0 ? (
            <p className="mt-1 text-xs text-secondary-500">
              Scoped to facility billing for: {overviewData.billingFacilities.map((facility) => facility.name).join(', ')}
            </p>
          ) : null}
          {isUsingOrganizationFallback ? (
            <p className="mt-1 text-xs text-secondary-500">
              Showing organization-level service state because this facility is covered by organization services.
            </p>
          ) : null}
          {billingAccessRestricted ? (
            <p className="mt-1 text-xs text-secondary-500">
              Financial records are hidden for your current billing scope. Service state is still shown where applicable.
            </p>
          ) : null}
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          <Link to="/settings?tab=organization&orgTab=service-access" className="text-primary-600 hover:text-primary-700 text-sm inline-flex items-center gap-1">
            <ArrowRightCircle size={15} />
            Request service change
          </Link>
          <Link to="/billing/payment-methods" className="text-primary-600 hover:text-primary-700 text-sm inline-flex items-center gap-1">
            <CreditCard size={15} />
            Manage payment methods
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 md:gap-4">
        <div className="card p-5 xl:col-span-1">
          <div className="flex items-center gap-2 text-secondary-800 font-semibold"><Activity size={16} /> Account Summary</div>
          <div className="mt-3 md:mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
            <div className="rounded-lg bg-secondary-50 p-3">
              <p className="text-xs text-secondary-500">Outstanding</p>
              <p className="text-lg font-bold text-secondary-900">${totalOutstanding.toLocaleString()}</p>
            </div>
            <div className="rounded-lg bg-secondary-50 p-3">
              <p className="text-xs text-secondary-500">Overdue</p>
              <p className="text-lg font-bold text-red-600">{paymentCounts.overdue}</p>
            </div>
            <div className="rounded-lg bg-secondary-50 p-3">
              <p className="text-xs text-secondary-500">Paid</p>
              <p className="text-lg font-bold text-green-600">{paymentCounts.paid}</p>
            </div>
          </div>
          <p className="mt-3 text-xs text-secondary-500">
            {isFacilityUser ? 'Showing invoices your facility is directly responsible for paying' : 'Current invoice status snapshot'}
          </p>
        </div>

        <div className="card p-5">
          <div className="flex items-center gap-2 text-secondary-700 font-semibold"><Layers size={16} /> LMS Subscription</div>
          <p className="mt-1 text-xs text-secondary-500">Commercial coverage, seat pool, and usage</p>
          <div className="mt-3 space-y-1 text-sm text-secondary-600">
            <p>Plan: <span className="font-medium text-secondary-800">{lmsPlanLabel}</span></p>
            <p>Commercial scope: <span className="font-medium text-secondary-800">{formatCoverageText(lmsCoverage)}</span></p>
            {formatFacilityNames(lmsCoverage) ? (
              <p>Covered facilities: <span className="font-medium text-secondary-800">{formatFacilityNames(lmsCoverage)}</span></p>
            ) : null}
            <p>Delivery: <span className="font-medium text-secondary-800">{formatDeliveryLabel(lmsCoverage.deliveryMode)}</span></p>
            <p>Distribution: <span className="font-medium text-secondary-800 capitalize">{formatDistributionLabel(lmsCoverage.distributionMode)}</span></p>
            <p>Seat pool: <span className="font-medium text-secondary-800">{formatSeatPool(facilityEntitlements?.lms?.usage)}</span></p>
          </div>
          {hideOrgWideUsage ? (
            <p className="mt-2 text-xs text-secondary-500">Usage details are hidden for organization-covered facilities.</p>
          ) : (
            <>
              <p className="mt-2 text-sm text-secondary-600">
                Seats used: <span className="font-medium text-secondary-800">{lmsSeatsUsed}</span>
                {lmsSeatsLimit
                  ? ` / ${lmsSeatsLimit}`
                  : ' / unlimited'}
              </p>
              <p className="mt-1 text-xs text-secondary-500">Remaining: {lmsSeatsRemaining}</p>
              <p className="mt-1 text-xs text-secondary-500">Seat activity: {lmsSeatActivity.summary || 'Not available'}</p>
            </>
          )}
          {inheritedEntitlements && (
            <div className="mt-4 border-t border-secondary-200 pt-3">
              <p className="text-xs text-secondary-500 mb-1">Inherited from organization</p>
              <div className="space-y-1 text-sm text-secondary-600">
                <p>Plan: <span className="font-medium text-secondary-800">{inheritedLmsPlanLabel || 'No active plan'}</span></p>
                <p>Commercial scope: <span className="font-medium text-secondary-800">{formatCoverageText(inheritedLmsCoverage)}</span></p>
                {formatFacilityNames(inheritedLmsCoverage) ? (
                  <p>Covered facilities: <span className="font-medium text-secondary-800">{formatFacilityNames(inheritedLmsCoverage)}</span></p>
                ) : null}
                <p>Delivery: <span className="font-medium text-secondary-800">{formatDeliveryLabel(inheritedLmsCoverage.deliveryMode)}</span></p>
                <p>Distribution: <span className="font-medium text-secondary-800 capitalize">{formatDistributionLabel(inheritedLmsCoverage.distributionMode)}</span></p>
                <p>Seat pool: <span className="font-medium text-secondary-800">{formatSeatPool(inheritedEntitlements?.lms?.usage)}</span></p>
              </div>
              {!hideOrgWideUsage ? (
                <>
                  <p className="mt-1 text-sm text-secondary-600">
                    Seats used: <span className="font-medium text-secondary-800">{inheritedLmsSeatsUsed}</span>
                    {inheritedLmsSeatsLimit
                      ? ` / ${inheritedLmsSeatsLimit}`
                      : ' / unlimited'}
                  </p>
                  <p className="mt-1 text-xs text-secondary-500">Remaining: {inheritedLmsSeatsRemaining}</p>
                  <p className="mt-1 text-xs text-secondary-500">Seat activity: {inheritedSeatActivity.summary || 'Not available'}</p>
                </>
              ) : null}
            </div>
          )}
          <p className="mt-3 text-xs text-secondary-500">Subscription coverage is separate from course rollout. Headquarters may release courses operationally, but coverage still controls which facilities are commercially included.</p>
        </div>

        {showConsultingEntitlements && <div className="card p-5">
          <div className="flex items-center gap-2 text-secondary-700 font-semibold"><Users size={16} /> Consulting Entitlements</div>
          <p className="mt-1 text-xs text-secondary-500">Purchased scope</p>
          {facilityEntitlements?.consulting?.coverage && (
            <div className="mt-3 space-y-1 text-sm text-secondary-600">
              <p>Commercial scope: <span className="font-medium text-secondary-800">{formatCommercialAppliesTo(facilityEntitlements.consulting.coverage)}</span></p>
              {formatFacilityNames(facilityEntitlements.consulting.coverage) && (
                <p>Covered facilities: <span className="font-medium text-secondary-800">{formatFacilityNames(facilityEntitlements.consulting.coverage)}</span></p>
              )}
            </div>
          )}
          <p className="mt-3 text-sm text-secondary-600">{consultingCategories.length} enabled category(ies)</p>
          <p className="mt-1 text-sm text-secondary-600">{consultingEffectiveServices.length} enabled subcategory item(s)</p>

          <div className="mt-3">
            <p className="text-xs text-secondary-500 mb-1">Categories</p>
            {consultingCategories.length ? (
              <div className="flex flex-wrap items-center gap-1.5">
                {visibleCategoryChips.map((category) => (
                  <span key={`cat-${category}`} className="px-2 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs">
                    {category}
                  </span>
                ))}
                {remainingCategoryCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowAllCategories(true)}
                    className="px-2 py-1 rounded-full bg-secondary-50 text-secondary-600 text-xs hover:bg-secondary-100"
                  >
                    +{remainingCategoryCount} more
                  </button>
                )}
                {showAllCategories && consultingCategories.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setShowAllCategories(false)}
                    className="px-2 py-1 rounded-full bg-secondary-50 text-secondary-600 text-xs hover:bg-secondary-100"
                  >
                    Show less
                  </button>
                )}
              </div>
            ) : (
              <p className="text-xs text-secondary-500">No categories selected</p>
            )}
          </div>

          <div className="mt-3">
            <p className="text-xs text-secondary-500 mb-1">Subcategories</p>
            {consultingEffectiveServices.length ? (
              <div className="flex flex-wrap items-center gap-1.5">
                {visibleSubcategoryChips.map((subcategory) => (
                  <span key={`sub-${subcategory}`} className="px-2 py-1 rounded-full bg-primary-50 text-primary-700 text-xs">
                    {subcategory}
                  </span>
                ))}
                {remainingSubcategoryCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowAllSubcategories(true)}
                    className="px-2 py-1 rounded-full bg-secondary-50 text-secondary-600 text-xs hover:bg-secondary-100"
                  >
                    +{remainingSubcategoryCount} more
                  </button>
                )}
                {showAllSubcategories && consultingEffectiveServices.length > 4 && (
                  <button
                    type="button"
                    onClick={() => setShowAllSubcategories(false)}
                    className="px-2 py-1 rounded-full bg-secondary-50 text-secondary-600 text-xs hover:bg-secondary-100"
                  >
                    Show less
                  </button>
                )}
              </div>
            ) : (
              <p className="text-xs text-secondary-500">No subcategories selected</p>
            )}
          </div>

          <p className="mt-2 text-xs text-secondary-500">
            Retainer: {facilityEntitlements?.consulting?.retainer?.enabled
              ? `$${Number(facilityEntitlements?.consulting?.retainer?.amount || 0).toLocaleString()} ${facilityEntitlements?.consulting?.retainer?.frequency}`
              : 'not enabled'}
          </p>
          {inheritedEntitlements && (
            <div className="mt-4 border-t border-secondary-200 pt-3">
              <p className="text-xs text-secondary-500 mb-1">Inherited from organization</p>
              <p className="mt-1 text-sm text-secondary-600">{inheritedConsultingCategories.length} enabled category(ies)</p>
              <p className="mt-1 text-sm text-secondary-600">{inheritedConsultingEffectiveServices.length} enabled subcategory item(s)</p>
              {inheritedConsultingCategories.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-secondary-500 mb-1">Categories</p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {inheritedConsultingCategories.map((category) => (
                      <span key={`inh-cat-${category}`} className="px-2 py-1 rounded-full bg-secondary-50 text-secondary-600 text-xs">
                        {category}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {inheritedConsultingEffectiveServices.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-secondary-500 mb-1">Subcategories</p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {inheritedConsultingEffectiveServices.map((sub) => (
                      <span key={`inh-sub-${sub}`} className="px-2 py-1 rounded-full bg-secondary-50 text-secondary-600 text-xs">
                        {sub}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>}
      </div>

      <div className="card p-3 md:p-4 border border-secondary-200 sticky top-20 z-10 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-secondary-900 flex items-center gap-2"><FileText size={16} /> Invoices</h2>
          <p className="text-xs text-secondary-500">Filter by payment status</p>
        </div>
        <div className="flex overflow-x-auto gap-1 p-1 md:p-2">
          {[
            { id: 'all', label: 'All Invoices', icon: null },
            { id: 'unpaid', label: 'Unpaid', icon: Clock },
            { id: 'overdue', label: 'Overdue', icon: AlertCircle },
            { id: 'partial', label: 'Partial Payment', icon: Clock },
            { id: 'paid', label: 'Paid', icon: CheckCircle2 },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setPaymentView(id)}
              className={`px-3 md:px-4 py-2 rounded-lg text-sm md:text-base font-medium whitespace-nowrap transition-colors flex items-center gap-2 border ${
                paymentView === id
                  ? 'bg-secondary-200 text-secondary-900 border-secondary-300'
                  : 'bg-white text-secondary-700 border-secondary-200 hover:bg-secondary-50'
              }`}
            >
              {Icon && <Icon size={18} />}
              {label}
              <span className={`text-sm font-semibold ${paymentView === id ? 'text-secondary-900' : 'text-secondary-600'}`}>
                {paymentCounts[id]}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between px-6 py-3 border-b border-secondary-200">
          <p className="text-sm text-secondary-600">Showing {invoicePagination.items.length} of {invoicePagination.total} invoices</p>
          <div className="flex items-center gap-2">
            <span className="text-sm text-secondary-600">Per page</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">Show all</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : filteredInvoices.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="responsive-table w-full">
              <thead className="bg-secondary-50 border-b border-secondary-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-secondary-500 uppercase tracking-wider">Invoice</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-secondary-500 uppercase tracking-wider">Bill To</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-secondary-500 uppercase tracking-wider">Amount</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-secondary-500 uppercase tracking-wider">Due Date</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-secondary-500 uppercase tracking-wider">Status</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-secondary-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-200">
                {invoicePagination.items.map((invoice) => (
                  <tr key={invoice._id} className="hover:bg-secondary-50">
                    <td data-label="Invoice" className="px-6 py-4">
                      <Link to={`/invoices/${invoice._id}`} className="font-medium text-primary-600 hover:text-primary-700">
                        {invoice.invoiceNumber}
                      </Link>
                      {invoice.clientId?.name ? (
                        <div className="mt-1 text-xs text-secondary-500">{invoice.clientId.name}</div>
                      ) : null}
                    </td>
                    <td data-label="Bill to" className="px-6 py-4 text-secondary-600">
                      <div className="font-medium text-secondary-900">{getBillToName(invoice)}</div>
                      {getBillToMeta(invoice) ? (
                        <div className="mt-1 text-xs text-secondary-500">{getBillToMeta(invoice)}</div>
                      ) : null}
                    </td>
                    <td data-label="Amount" className="px-6 py-4 font-medium text-secondary-900">
                      ${invoice.total?.toLocaleString()}
                    </td>
                    <td data-label="Due date" className="px-6 py-4 text-secondary-600">
                      {new Date(invoice.dueDate).toLocaleDateString()}
                    </td>
                    <td data-label="Status" className="px-6 py-4">
                      <span className={statusColors[invoice.status] || 'badge-secondary'}>
                        {invoice.status}
                      </span>
                    </td>
                    <td data-label="Actions" className="px-6 py-4 text-right">
                      <Link to={`/invoices/${invoice._id}`} className="text-secondary-600 hover:text-secondary-900">
                        View
                      </Link>
                      {invoice.stripePaymentLink && (
                        <a href={invoice.stripePaymentLink} target="_blank" rel="noopener noreferrer" className="ml-4 text-primary-600 hover:text-primary-700">
                          <ExternalLink size={16} className="inline" />
                          Pay
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center text-secondary-500">
            <Receipt className="w-12 h-12 mx-auto mb-4 text-secondary-300" />
            <p>No invoices found in this view</p>
          </div>
        )}
        {filteredInvoices.length > 0 && invoicePagination.pages > 1 && pageSize !== 'all' && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-secondary-200">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={invoicePagination.currentPage === 1}
              className="px-3 py-2 rounded-lg border text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-secondary-600">Page {invoicePagination.currentPage} of {invoicePagination.pages}</span>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(invoicePagination.pages, prev + 1))}
              disabled={invoicePagination.currentPage === invoicePagination.pages}
              className="px-3 py-2 rounded-lg border text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Payment history */}
      <div className="card">
        <div className="p-4 border-b border-secondary-200 flex items-center justify-between">
          <h2 className="font-semibold text-secondary-900">Payment History</h2>
          <button
            type="button"
            onClick={() => setShowPaymentHistory((prev) => !prev)}
            className="inline-flex items-center gap-1 text-sm text-secondary-600 hover:text-secondary-900"
          >
            {showPaymentHistory ? 'Hide' : 'Show'}
            {showPaymentHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
        {!showPaymentHistory ? (
          <div className="p-6 text-secondary-500 text-sm">Payment history is collapsed.</div>
        ) : overviewData?.paymentHistory?.length ? (
          <div>
            <div className="flex items-center justify-between px-6 py-3 border-b border-secondary-200">
              <p className="text-sm text-secondary-600">Showing {historyPagination.items.length} of {historyPagination.total} payments</p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-secondary-600">Per page</span>
                <select
                  value={historyPageSize}
                  onChange={(e) => setHistoryPageSize(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="all">Show all</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
            </div>
          <div className="overflow-x-auto">
            <table className="responsive-table w-full">
              <thead className="bg-secondary-50 border-b border-secondary-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-secondary-500 uppercase tracking-wider">Date</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-secondary-500 uppercase tracking-wider">Invoice</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-secondary-500 uppercase tracking-wider">Method</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-secondary-500 uppercase tracking-wider">Reference</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-secondary-500 uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-200">
                {historyPagination.items.map((payment, idx) => (
                  <tr key={`${payment.invoiceId}-${idx}`} className="hover:bg-secondary-50">
                    <td data-label="Date" className="px-6 py-4 text-secondary-600">{new Date(payment.paidAt).toLocaleDateString()}</td>
                    <td data-label="Invoice" className="px-6 py-4">
                      <Link to={`/invoices/${payment.invoiceId}`} className="text-primary-600 hover:text-primary-700">
                        {payment.invoiceNumber}
                      </Link>
                    </td>
                    <td data-label="Method" className="px-6 py-4 text-secondary-600">{payment.method || '-'}</td>
                    <td data-label="Reference" className="px-6 py-4 text-secondary-600 break-all">{payment.reference || '-'}</td>
                    <td data-label="Amount" className="px-6 py-4 text-right font-medium text-secondary-900">${Number(payment.amount || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {historyPagination.pages > 1 && historyPageSize !== 'all' && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-secondary-200">
              <button
                type="button"
                onClick={() => setHistoryPage((prev) => Math.max(1, prev - 1))}
                disabled={historyPagination.currentPage === 1}
                className="px-3 py-2 rounded-lg border text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-secondary-600">Page {historyPagination.currentPage} of {historyPagination.pages}</span>
              <button
                type="button"
                onClick={() => setHistoryPage((prev) => Math.min(historyPagination.pages, prev + 1))}
                disabled={historyPagination.currentPage === historyPagination.pages}
                className="px-3 py-2 rounded-lg border text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
          </div>
        ) : (
          <div className="p-6 text-secondary-500">No payments recorded yet.</div>
        )}
      </div>
    </div>
  );
}
